import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSelection } from '@/contexts/SelectionContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { useTotalProducts } from './useTotalProducts';
import { useMcMembers } from './useMcMembers';
import { getRecommendedPlan, getNextPlan, isSpecialPlan, type PlanInfo } from '@/lib/plans';
import { readPlanCache, writePlanCache } from '@/lib/planCache';

/**
 * Eine Zeile aus `check_can_optimize` (v4, Bestandsmass).
 *
 * WICHTIG — die Semantik hat sich mit v4 gedreht: `optimized_count` ist
 * weiterhin der LIFETIME-Verbrauchszaehler (`user_optimization_usage`), aber
 * das Tarif-Limit misst seither den BESTAND: `managed_count` ist die Zahl der
 * aktuell betreuten (= optimierten und noch im Feed liegenden) Produkte,
 * `remaining_capacity` der freie Platz darunter. Wer ein Produkt aus seinem
 * Feed nimmt, gibt seinen Platz zurueck — das konnte der Lifetime-Zaehler nie.
 * Gegen Dauerlaeufe steht daneben ein Fair-Use-Fenster ueber die letzten
 * 30 Tage (`fair_use_used` / `fair_use_limit`), das nur sperrt und nie trimmt.
 */
type EnforcementRow = {
  can_optimize: boolean;
  optimized_count: number;
  product_limit: number;
  plan_tier: string;
  usage_pct: number;
  phase: string;
  managed_count: number;
  remaining_capacity: number;
  fair_use_used: number;
  fair_use_limit: number;
  fair_use_resets_at: string | null;
};

export function useEnforcement() {
  const { user } = useAuth();
  const { totalProducts } = useTotalProducts();

  // MC-Mitgliedschaft Stufe 1 (Task 12): das Kontingent gehoert der Merchant
  // Center, nicht dem angemeldeten Nutzer. Ist der Nutzer nur MITGLIED der
  // aktiven MC, muss `check_can_optimize` gegen deren EIGENTUEMER laufen —
  // sonst saehe ein Mitglied sein eigenes, i.d.R. leeres Free-Kontingent
  // statt des tatsaechlich gebuchten Bands der MC. Scheitert die Mitglieder-
  // Abfrage selbst (Netzfehler, RPC-Fehler), liefert `useMcMembers` `members: []`
  // statt zu werfen — der Owner bleibt dann unbekannt, `billedUserId` faellt
  // fail-open auf den angemeldeten Nutzer zurueck, und der Nutzer sieht sein
  // EIGENES, echtes Kontingent statt des der MC (nie erfundene Zahlen, nur die
  // falsche Person).
  const { selectedMerchantCenterId } = useSelection();
  const { members } = useMcMembers(selectedMerchantCenterId);
  const owner = useMemo(
    () => members.find((m) => m.role === 'owner') ?? null,
    [members],
  );
  // Solange die Mitgliederliste noch laedt (oder keine MC ausgewaehlt ist),
  // ist der Owner unbekannt — die Abfrage faellt auf den angemeldeten Nutzer
  // zurueck und BLOCKIERT NICHT darauf, dass `useMcMembers` fertig ist. Loest
  // der Owner spaeter auf, aendert sich `billedUserId`, der Query-Key wechselt
  // mit und die Abfrage laedt automatisch neu.
  const billedUserId = owner?.user_id ?? user?.id;
  // `null` heisst "kein fremdes Kontingent" — sowohl wenn der Owner noch
  // unbekannt ist als auch wenn er der angemeldete Nutzer selbst ist.
  const billedOwnerEmail = owner && user && owner.user_id !== user.id ? owner.email : null;
  const isMemberOfForeignMc = billedOwnerEmail != null;

  // Seed the last-known plan from localStorage so it renders instantly on reload
  // instead of waiting for the check_can_optimize RPC. React Query treats it as
  // stale (initialDataUpdatedAt) and refetches in the background.
  const seed = useMemo(
    () => readPlanCache<EnforcementRow>('enforcement', billedUserId),
    [billedUserId],
  );

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['enforcement', billedUserId],
    queryFn: async () => {
      if (!billedUserId) return null;
      const { data, error } = await supabase.rpc('check_can_optimize', {
        p_user_id: billedUserId,
      });
      if (error) throw error;
      const row = (data?.[0] ?? null) as EnforcementRow | null;
      // Vor Migration B verweigert der Ownership-Guard in `check_can_optimize`
      // eine fremde `p_user_id` und liefert eine leere Menge zurueck — `row`
      // ist dann `null`, und die Rueckgabe unten faellt fail-open auf die
      // Vorgabewerte zurueck (Label ohne belastbare Zahlen). Erst nach
      // Migration B (v5) beantwortet die RPC eine fremde `p_user_id`.
      if (row) writePlanCache('enforcement', billedUserId, row);
      return row;
    },
    enabled: !!billedUserId,
    staleTime: 30_000,
    initialData: seed?.data,
    initialDataUpdatedAt: seed?.updatedAt,
  });

  // fail-open: solange die RPC laeuft (oder ausfaellt) blockiert das Frontend
  // nichts. Das Gate ist der Server — `enqueue-optimization-batches` prueft die
  // Kapazitaet selbst und antwortet mit CAPACITY_REACHED / FAIR_USE_REACHED.
  const canOptimize = data?.can_optimize ?? true;
  // `count` ist seit dem Bestandsmass der BESTAND, nicht der Lifetime-Verbrauch:
  // "du betreust N von M Produkten". Alle Anzeigen (UsageWidget, UpgradeModal,
  // Fortschrittsbalken) lesen diese Zahl — mit `optimized_count` zeigten sie
  // einen Zaehler, der nie wieder faellt, obwohl der Platz laengst frei ist.
  const count = Number(data?.managed_count ?? 0);
  const limit = data?.product_limit ?? 200;
  const plan = data?.plan_tier ?? 'free';
  // Free-Gates (Schedules, CustomInstructions) duerfen NUR sperren, wenn der
  // Plan positiv bekannt ist: bei RPC-Fehler liefert der Query data=undefined
  // und `plan` faellt auf 'free' zurueck — ein zahlender Kunde saehe sonst
  // gesperrte Controls (Review 2a117de MINOR-1). Das harte Gate bleibt der
  // Server; die UI-Sperre ist Komfort und darf im Zweifel offen bleiben.
  const planKnown = data?.plan_tier != null;
  const usagePct = Number(data?.usage_pct ?? 0);
  const phase = (data?.phase ?? 'GREEN') as 'GREEN' | 'YELLOW' | 'RED';
  // Der Server rechnet `remaining_capacity` selbst; der lokale Rueckfall greift
  // nur, solange keine Zeile da ist (oder eine alte RPC-Fassung antwortet).
  const remainingCapacity = Number(data?.remaining_capacity ?? Math.max(0, limit - count));
  const fairUseUsed = Number(data?.fair_use_used ?? 0);
  // 500 = floor(200 x 2,5): das Free-Limit unter der k(N)-Staffel v4.1
  // (Migration `20260820100400_fair_use_k_v41.sql`, k = 2,5 bis 2.000 Produkte).
  // Der Rueckfall greift nur, solange keine Zeile da ist — er muss trotzdem zum
  // Default-Limit daneben (`product_limit ?? 200`) passen, sonst zeigt die UI in
  // der Ladephase ein Fair-Use-Budget, das es so nie gab. Vorher stand hier 600,
  // der Wert der verworfenen v4-Staffel (k = 3).
  const fairUseLimit = Number(data?.fair_use_limit ?? 500);
  // Kein Default-Datum: NULL heisst "kein Fenster offen / unbekannt" und darf
  // nicht als konkreter Termin erscheinen.
  const fairUseResetsAt = data?.fair_use_resets_at ?? null;

  // UX-Audit K38/K48 (D7): ein Sondertarif (Agentur, Legacy, manuell erhoehtes
  // Limit) und ein fremdes Kontingent bekommen KEINE Band-Empfehlung. Beim
  // Sondertarif wuerde der Kauf das Limit senken, beim Mitglied erweitert er
  // das Kontingent des Eigentuemers gar nicht. Alle Upsell-Flaechen, die an
  // recommendedPlan/nextPlan haengen, verschwinden damit automatisch; der
  // Kapazitaets-Dialog zeigt stattdessen Kontakt bzw. den Eigentuemer.
  const billedPlanIsSpecial = planKnown && isSpecialPlan(plan, data?.product_limit ?? null);
  const upsellBlocked = billedPlanIsSpecial || isMemberOfForeignMc;
  const recommendedPlan: PlanInfo | null = upsellBlocked ? null : getRecommendedPlan(totalProducts, plan);
  const nextPlan: PlanInfo | null = upsellBlocked ? null : getNextPlan(plan);

  return {
    canOptimize,
    count,
    limit,
    plan,
    usagePct,
    phase,
    remainingCapacity,
    fairUseUsed,
    fairUseLimit,
    fairUseResetsAt,
    nextPlan,
    recommendedPlan,
    totalProducts,
    planKnown,
    billedOwnerEmail,
    isMemberOfForeignMc,
    billedPlanIsSpecial,
    isLoading,
    refetch,
  };
}
